import deepCopy from 'deepcopy';
import {
	getSavedLoadPlanningSearchesRequest,
	newSavedLoadPlanningSearchesRequest,
	updateMultipleSavedLoadPlanningSearchesRequest,
	updateSingleSavedLoadPlanningSearchRequest,
	deleteSavedLoadPlanningSearchRequest,
	getSavedLoadPlanningSearchCountRequest,
} from '../requests';
import { getClerkId } from '@/core';
import rdbClient from 'rdb-client';

async function init({ state, commit, dispatch }) {
	await dispatch('fetchSavedSearches');
	if (state.autoSearches.length) {
		commit('set', (state) => {
			state.searchInterval = setInterval(() => { dispatch('autoSearch'); }, 60000);
		});
	}
}

async function dismount({ commit }) {
	commit('set', (state) => { clearInterval(state.searchInterval); });
}

async function fetchSavedSearches({ commit, dispatch }) {
	let clerkId = await getClerkId();
	try {
		let response = await getSavedLoadPlanningSearchesRequest({ clerkId });
		commit('setSavedSearches', response);
		dispatch('autoSearch');
	} catch (err) {
		throw new Error('failedToFetch');
	}
}

async function getSettingsSavedSearches({ commit }) {
	let clerkId = await getClerkId();
	try {
		let response = await getSavedLoadPlanningSearchesRequest({ clerkId });
		commit('set', (state) => state.settingsSavedSearches = response);
	} catch (err) {
		console.log(`Fetch saved order searches failed: ${  err}`);
	}
}

async function autoSearch({ state, commit }) {
	if (state.liveUpdateInProgress) return;
	commit('set', (state) => { state.liveUpdateInProgress = true; });
	// Force request to be allowed if no return after minute
	setTimeout(() => {
		commit('set', (state) => { state.liveUpdateInProgress = false; });
	}, 60000);
	state.autoSearches.forEach(async search => {
		let ordersCount = await getSavedLoadPlanningSearchCountRequest(search.id);
		commit('setOrdersCount', { id: search.id, ordersCount: ordersCount });
	});
	commit('set', (state) => { state.liveUpdateInProgress = false; });
}

async function addSavedSearch({ dispatch }, { searchOptions, orderFilter }) {
	let clerkId = await getClerkId();
	let orderOptions = searchOptions.orderSearch;
	let tripOptions = searchOptions.tripSearch;
	let newSearch = {
		clerkId,
		label: searchOptions.label,
		mainMenu: searchOptions.mainMenu,
		isPublic: searchOptions.isPublic,
		autoSearch: searchOptions.autoSearch,
		count: 0,
		orderSearch: {
			timeInterval: {
				before: orderOptions.timeInterval.before,
				after:  orderOptions.timeInterval.useAfter ? orderOptions.timeInterval.after : ''
			},
			dateType: orderFilter.dateType,
			searchAreas: orderOptions.searchAreas || [],
			fromDespatchParty: orderOptions.fromDespatchParty || [],
			toDeliveryParty: orderOptions.toDeliveryParty || [],
			customer: orderOptions.customer || [],
			goodsTypes: orderOptions.goodsTypes || [],
			orderTypes: orderOptions.orderTypes || [],
			economicalStatus: orderOptions.economicalStatus || [],
			orderStatus: orderOptions.orderStatus || [],
			references: orderOptions.references || [],
			referencesText: orderOptions.referencesText,
			planned: orderOptions.planned,
			hasAtd: orderOptions.hasAtd,
			waybillNumber: orderOptions.waybillNumber || [],
			mustHaveInboundEdi: orderOptions.mustHaveInboundEdi,
			inboundEdi: orderOptions.inboundEdi,
			mustHaveDeviation: orderOptions.mustHaveDeviation,
		},
		tripSearch: {
			fromLocation: tripOptions.fromLocation || [],
			toLocation: tripOptions.toLocation || [],
			tripType: tripOptions.tripType || [],
			status: tripOptions.status || [],
			cargoCarrier: tripOptions.cargoCarrier || [],
			timeInterval: {
				before: tripOptions.timeInterval.before,
				after:  tripOptions.timeInterval.useAfter ? tripOptions.timeInterval.after : ''
			}
		}
	};
	try {
		let response = await newSavedLoadPlanningSearchesRequest(newSearch);
		dispatch('fetchSavedSearches');
		dispatch('autoSearch');
		return response;
	} catch (err) {
		throw new Error('failedToSave');
	}
}

async function updateSavedSearch({ state: { savedSearches }, commit, dispatch }, { searchId, searchOptions }) {

	let oldSavedSearch = savedSearches.find(s => s.id === searchId);

	if (!oldSavedSearch || searchId === -1) throw new Error('failedToSave');

	let orderOptions = searchOptions.orderSearch;
	let tripOptions = searchOptions.tripSearch;

	let { orderSearch, tripSearch } = oldSavedSearch;
	let newSavedSearch = {
		...deepCopy(oldSavedSearch),
		label: searchOptions.label,
		mainMenu: searchOptions.mainMenu,
		isPublic: searchOptions.isPublic,
		autoSearch: searchOptions.autoSearch,
		orderSearch: {
			timeInterval: {
				before: orderOptions.timeInterval.before,
				after:  orderOptions.timeInterval.useAfter ? orderOptions.timeInterval.after : orderSearch.timeInterval.after
			},
			dateType: orderSearch.dateType,
			searchAreas: orderOptions.searchAreas || [],
			fromDespatchParty: orderOptions.fromDespatchParty || [],
			toDeliveryParty: orderOptions.toDeliveryParty || [],
			customer: orderOptions.customer || [],
			goodsTypes: orderOptions.goodsTypes || [],
			orderTypes: orderOptions.orderTypes || [],
			economicalStatus: orderOptions.economicalStatus || [],
			orderStatus: orderOptions.orderStatus || [],
			references: orderOptions.references || [],
			referencesText: orderOptions.referencesText,
			planned: orderOptions.planned,
			hasAtd: orderOptions.hasAtd,
			waybillNumber: orderOptions.waybillNumber || [],
			mustHaveInboundEdi: orderOptions.mustHaveInboundEdi,
			inboundEdi: orderOptions.inboundEdi,
			mustHaveDeviation: orderOptions.mustHaveDeviation,
		},
		tripSearch: {
			fromLocation: tripOptions.fromLocation || [],
			toLocation: tripOptions.toLocation || [],
			tripType: tripOptions.tripType || [],
			status: tripOptions.status || [],
			cargoCarrier: tripOptions.cargoCarrier || [],
			timeInterval: {
				before: tripOptions.timeInterval.before,
				after:  tripOptions.timeInterval.useAfter ? tripOptions.timeInterval.after : tripSearch.timeInterval.after
			}
		}
	};

	try {
		let patch = rdbClient.createPatch(oldSavedSearch, newSavedSearch);
		if (!patch.length) {
			throw new Error('emptyPatch');
		}

		await updateSingleSavedLoadPlanningSearchRequest(newSavedSearch.id, patch);
		oldSavedSearch.label = newSavedSearch.label;
		dispatch(`loadPlanning/${newSavedSearch.id}/loadSavedSearch`, newSavedSearch, { root: true });
		dispatch(`loadPlanning/${newSavedSearch.id}/orderSearch/fetchOrders`, null, { root: true });
		dispatch(`loadPlanning/${newSavedSearch.id}/tripSearch/fetchTrips`, null, { root: true });
		commit('updateSavedSearch', newSavedSearch);

		if (patch.length > 1 || patch[0].path !== '/data/label') {
			let ordersCount = await getSavedLoadPlanningSearchCountRequest(searchId);
			commit('setOrdersCount', { id: searchId, ordersCount });
		}
	} catch (err) {
		if (err.message === 'emptyPatch') throw err;
		throw new Error('failedToSave');
	}
}

async function deleteSavedSearch({ dispatch }, searchId) {
	try {
		await deleteSavedLoadPlanningSearchRequest(searchId);
		dispatch('fetchSavedSearches');
	} catch (error) {
		throw new Error('failedToDelete');
	}
}

async function updateSavedSearchesOnMainMenu({ state, commit, dispatch }, items) {
	let savedSearches = state.savedSearches.filter(x => x.id !== -1);

	let originals = [];
	let updated = [];
	for (let item of savedSearches) {
		originals.push(deepCopy(item));
		item.mainMenu = false;
		item.autoSearch = false;
		updated.push(deepCopy(item));
	}

	for (let item of items) {
		let updatedItem = updated.find(x => x.id === item.id);
		updatedItem.mainMenu = true;
		updatedItem.autoSearch = true;
	}

	let patch = rdbClient.createPatch(originals, updated);

	try {
		let response = await updateMultipleSavedLoadPlanningSearchesRequest(patch);
		commit('setSavedSearches', response);
		dispatch('autoSearch');
	} catch (error) {
		console.log(`Failed to update searches: ${  error}`);
	}
}

export default {
	init,
	dismount,
	fetchSavedSearches,
	getSettingsSavedSearches,
	autoSearch,
	addSavedSearch,
	updateSavedSearch,
	deleteSavedSearch,
	updateSavedSearchesOnMainMenu,
};